/**
 * Home view mode: grid (2-3 cards per row) or list (1 per row).
 * Persists to localStorage.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'abc-home-view-mode';

export type HomeViewMode = 'grid' | 'list';

function loadViewMode(): HomeViewMode {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    if (s === 'feed') {
      localStorage.setItem(STORAGE_KEY, 'list');
      return 'list';
    }
    return s === 'grid' ? 'grid' : 'list';
  } catch {
    return 'list';
  }
}

function saveViewMode(mode: HomeViewMode) {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // ignore
  }
}

type ContextValue = {
  viewMode: HomeViewMode;
  setViewMode: (mode: HomeViewMode) => void;
  toggleViewMode: () => void;
};

const HomeViewModeContext = createContext<ContextValue | null>(null);

export function HomeViewModeProvider({ children }: { children: React.ReactNode }) {
  const [viewMode, setViewModeState] = useState<HomeViewMode>(loadViewMode);

  useEffect(() => {
    saveViewMode(viewMode);
  }, [viewMode]);

  const setViewMode = useCallback((mode: HomeViewMode) => {
    setViewModeState(mode);
  }, []);

  const toggleViewMode = useCallback(() => {
    setViewModeState((prev) => (prev === 'grid' ? 'list' : 'grid'));
  }, []);

  return (
    <HomeViewModeContext.Provider value={{ viewMode, setViewMode, toggleViewMode }}>
      {children}
    </HomeViewModeContext.Provider>
  );
}

export function useHomeViewMode(): ContextValue {
  const ctx = useContext(HomeViewModeContext);
  if (!ctx) {
    return {
      viewMode: 'list',
      setViewMode: () => {},
      toggleViewMode: () => {},
    };
  }
  return ctx;
}
